import { inject, Injectable } from '@angular/core';
import { _, TranslateService } from '@ngx-translate/core';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { filter, map, Observable, switchMap, take } from 'rxjs';
import { IEvent } from '@app/api/interfaces/event.interface';
import { IPosition } from '@app/core/interfaces/position.interface';
import { ITranslationSubscription } from '@app/core/interfaces/localization';
import { DialogTrackerService } from '@app/core/services/dialog-tracker/dialog-tracker.service';
import { DialogEventComponent } from '@app/main/leak-detection/dialogs/dialog-event/dialog-event.component';
import { IDialogEventData } from '@app/main/leak-detection/dialogs/dialog-event/interfaces/dialog-event-data.interface';
import { DialogEventMode } from '@app/main/leak-detection/dialogs/dialog-event/enums/dialog-event-mode.enum';

interface IDialogEventLayout {
  width: string;
  breakpoints: Record<string, string>;
  fullScreenClass: string;
}

@Injectable({ providedIn: 'root' })
export class DialogEventService {
  private readonly dialogTrackerService = inject(DialogTrackerService);
  private readonly translateService = inject(TranslateService);

  public open(item: IEvent): DynamicDialogRef {
    const title = this.translateService.stream(_(`@@fp-event-header-title`), { I18N_VAR_EVENT_NO: item.serialNo });

    return this.openDialog({ mode: DialogEventMode.Edit, event: item, position: null }, title, {
      width: '42.8125rem',
      breakpoints: { '767.98px': '100vw' },
      fullScreenClass: 'max-md:!m-0 max-md:!max-w-none max-md:!h-dvh max-md:!max-h-none max-md:!rounded-none',
    });
  }

  public openCreate(position: IPosition): Observable<IEvent> {
    const title = this.translateService.stream(_(`@@fp-event-header-title-create`));

    const ref = this.openDialog({ mode: DialogEventMode.Create, event: null, position }, title, {
      width: '32.75rem',
      breakpoints: { '767.98px': '100vw' },
      fullScreenClass: 'max-md:!m-0 max-md:!max-w-none max-md:!h-dvh max-md:!max-h-none max-md:!rounded-none',
    });

    return ref.onClose.pipe(
      take(1),
      filter((created?: IEvent | null): created is IEvent => !!created),
      switchMap(created =>
        ref.onDestroy.pipe(
          take(1),
          map(() => created)
        )
      )
    );
  }

  private openDialog(
    data: Omit<IDialogEventData, 'title'>,
    title: ITranslationSubscription,
    { width, breakpoints, fullScreenClass }: IDialogEventLayout
  ): DynamicDialogRef {
    const baseDialogConfig = this.dialogTrackerService.getBaseDialogConfig();

    return this.dialogTrackerService.regAndOpen(DialogEventComponent, {
      ...baseDialogConfig,
      maskStyleClass: `!bg-(--p-neutral-500)/70 backdrop-blur-[3px] ${baseDialogConfig.maskStyleClass || ''}`,
      styleClass: `overflow-hidden !border-none ${fullScreenClass}`,
      width,
      breakpoints,
      showHeader: false,
      closeOnEscape: false,
      data: { ...data, title } satisfies IDialogEventData,
    });
  }
}
